// URL normalization and validation for the preview address bar.
// Kept separate from components so it can be unit tested in isolation.

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);
const BLOCKED_PROTOCOL_PATTERN = /^(javascript|data|file|vbscript|about|blob):/i;

const IPV4_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/;

function isIpv4(hostname) {
  if (!IPV4_PATTERN.test(hostname)) return false;
  return hostname.split(".").every((part) => Number(part) <= 255);
}

function looksLikeHost(hostname) {
  if (!hostname) return false;
  if (hostname === "localhost") return true;
  if (isIpv4(hostname)) return true;
  // Require at least one dot for "real" domains (example.com) so bare
  // words like "randomtext" aren't silently treated as valid hosts.
  return hostname.includes(".");
}

/**
 * Normalizes raw user input into a fully-qualified URL string, adding a
 * protocol when one is missing. Returns null when the input is empty.
 */
export function addProtocolIfMissing(input) {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  if (BLOCKED_PROTOCOL_PATTERN.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Validates and normalizes a URL string entered by the user.
 * @param {string} input
 * @returns {{ valid: boolean, url: string | null, hostname: string | null, error: string | null }}
 */
export function normalizeUrl(input) {
  if (typeof input !== "string" || !input.trim()) {
    return { valid: false, url: null, hostname: null, error: "Enter a URL to preview." };
  }

  const candidate = addProtocolIfMissing(input);

  if (BLOCKED_PROTOCOL_PATTERN.test(candidate)) {
    return {
      valid: false,
      url: null,
      hostname: null,
      error: "This protocol isn't supported for previewing.",
    };
  }

  let parsed;
  try {
    parsed = new URL(candidate);
  } catch {
    return { valid: false, url: null, hostname: null, error: "That doesn't look like a valid URL." };
  }

  if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) {
    return {
      valid: false,
      url: null,
      hostname: null,
      error: "Only http:// and https:// URLs can be previewed.",
    };
  }

  if (!looksLikeHost(parsed.hostname)) {
    return {
      valid: false,
      url: null,
      hostname: null,
      error: "Enter a full domain (example.com), localhost, or an IP address.",
    };
  }

  return { valid: true, url: parsed.toString(), hostname: parsed.hostname, error: null };
}

export function isValidUrl(input) {
  return normalizeUrl(input).valid;
}

export function getHostname(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
